/**
 * Browser error reporter for AgentAutofix (error-log-integration.md section 8.2).
 *
 * The server does NO dedup, so this carries the four required properties: a stable
 * fingerprint (digits / hex / UUIDs normalised), a one-per-hour throttle per
 * fingerprint, a 45s aggregation window, and a noise filter. Maps are bounded.
 * Fire-and-forget: no retries, no result handling beyond dropping the report.
 *
 * Only runs for signed-in users (the ingest requires an identity token), and
 * everything sent is run through redact() first.
 */

const HOST = "https://app.zeroexposure.io";
const SITE_KEY = "pk_TYBEddyi2Xho1usPtGjnZlC5WBBBpZIk"; // public, browser-safe key
const FLUSH_MS = 45_000;
const THROTTLE_MS = 60 * 60 * 1000;
const MAX_FINGERPRINTS = 500;
const MAX_REPORTS_PER_FLUSH = 20;

const API_BASE = import.meta.env.VITE_API_URL ? `${import.meta.env.VITE_API_URL}/api/v1` : "/api/v1";

interface Pending {
  count: number;
  first: number;
  last: number;
  err: Error;
}

const lastSent = new Map<string, number>();
const pending = new Map<string, Pending>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let installed = false;
let disabled = false;
let probe: Promise<boolean> | null = null;
/** Bumped on every uninstall so an in-flight flush can detect a sign-out and drop its batch. */
let generation = 0;

/**
 * Probe the token endpoint once per session. Anything but 200 (503 = AgentAutofix not
 * configured on this deployment, 401 = not signed in) disables the widget and the
 * reporter for the session. Feature off when unconfigured; no retries.
 */
export function probeAgentAutofix(): Promise<boolean> {
  const gen = generation;
  probe ??= fetch(`${API_BASE}/agentautofix/token`, { credentials: "include" })
    .then((r) => r.ok)
    .catch(() => false)
    .then((ok) => {
      if (!ok && gen === generation) disabled = true;
      return ok;
    });
  return probe;
}

/** Collapse UUIDs, hex runs and digit runs so ids in a message do not defeat the throttle. */
export function normalize(s: string): string {
  return String(s)
    .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, "{uuid}")
    .replace(/\b[0-9a-f]{6,}\b/gi, "{hex}")
    .replace(/\d+/g, "{n}");
}

export function fingerprint(err: Error): string {
  const frame = (err.stack || "").split("\n")[1] || "";
  return `${err.name}|${normalize(err.message || "")}|${normalize(frame.trim())}`;
}

/** Same rule table as the widget's redact(); over-redaction is the intended failure mode. */
export function redact(s: string): string {
  return String(s)
    .replace(/\b(bearer|basic|token)\s+[A-Za-z0-9._~+/=-]{8,}/gi, (_m, scheme: string) => `${scheme} [REDACTED]`)
    .replace(/\beyJ[A-Za-z0-9_-]{4,}\.[A-Za-z0-9_-]{4,}\.[A-Za-z0-9_-]{4,}/g, "[REDACTED:jwt]")
    .replace(
      /\b(?:sk|pk|rk|oat|olx|ghp|gho|ghu|ghs|ghr|github_pat|xox[baprs]|shpat|glpat|npm|AIza|SG)[-_][A-Za-z0-9_-]{8,}/g,
      "[REDACTED:apikey]",
    )
    .replace(
      /\b(password|passwd|pwd|secret|token|api[_-]?key)("?\s*[=:]\s*)(?!\[REDACTED)("[^"]{1,4096}"|'[^']{1,4096}'|[^\s,;&)\]}"']{1,4096})/gi,
      (_m, key: string, sep: string) => `${key}${sep}[REDACTED]`,
    );
}

/** Errors that are not ours to fix: extensions, benign browser warnings, cancelled fetches, third-party scripts. */
export function isNoise(err: Error, source?: string): boolean {
  const msg = err.message || "";
  if (/ResizeObserver loop/i.test(msg)) return true;
  if (/^(Script error\.?|Non-Error promise rejection captured)/i.test(msg)) return true;
  if (err.name === "AbortError" || /^(The user aborted a request|signal is aborted|The operation was aborted|Fetch is aborted|Request aborted)/i.test(msg)) return true;
  const haystack = `${source ?? ""}\n${err.stack ?? ""}`;
  if (/(chrome|moz|safari-web)-extension:\/\//i.test(haystack)) return true;
  // Third-party script: a source URL from another origin.
  if (source && /^https?:\/\//i.test(source) && !source.startsWith(location.origin)) return true;
  return false;
}

function evictIfNeeded(map: Map<string, unknown>): void {
  if (map.size <= MAX_FINGERPRINTS) return;
  const oldest = map.keys().next().value;
  if (oldest !== undefined) map.delete(oldest);
}

export function report(err: Error, source?: string): void {
  if (disabled || isNoise(err, source)) return;
  const fp = fingerprint(err);
  const now = Date.now();
  const lastAt = lastSent.get(fp);
  if (lastAt && now - lastAt < THROTTLE_MS) return;

  const agg = pending.get(fp);
  if (agg) {
    agg.count++;
    agg.last = now;
    return;
  }
  evictIfNeeded(pending);
  pending.set(fp, { count: 1, first: now, last: now, err });
  if (!flushTimer) flushTimer = setTimeout(() => void flush(), FLUSH_MS);
}

async function identityToken(): Promise<string | null> {
  try {
    const r = await fetch(`${API_BASE}/agentautofix/token`, { credentials: "include" });
    if (r.status === 503) disabled = true;
    return r.ok ? ((await r.json()) as { token: string }).token : null;
  } catch {
    return null;
  }
}

async function flush(): Promise<void> {
  flushTimer = null;
  if (disabled) {
    pending.clear();
    return;
  }
  const gen = generation;
  const batch = [...pending.entries()].slice(0, MAX_REPORTS_PER_FLUSH);
  pending.clear();
  if (!batch.length) return;

  const jwt = await identityToken();
  if (!jwt || gen !== generation) return; // not signed in / not configured: drop, the ingest requires identity

  for (const [fp, agg] of batch) {
    if (gen !== generation) return; // signed out / identity changed mid-flush: never send under another identity
    evictIfNeeded(lastSent);
    lastSent.set(fp, Date.now());
    const top = (agg.err.stack || "").split("\n")[1]?.trim() || "unknown";
    try {
      await fetch(`${HOST}/api/plugin/v1/comments`, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "x-zeroexposure-key": SITE_KEY,
          authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          comment: redact(
            `${agg.err.name}: ${agg.err.message}\n\n` +
              `${agg.count} occurrence(s) between ${new Date(agg.first).toISOString()} and ` +
              `${new Date(agg.last).toISOString()} on ${location.pathname}.\n\nTop frame: ${top}`,
          ).slice(0, 4000),
          x: 0,
          y: 0,
          // origin + pathname only, never location.href (query strings carry PII)
          page_url: redact(`${location.origin}${location.pathname}`).slice(0, 2000),
          element_meta: {
            kind: "error",
            fingerprint: redact(fp),
            occurrence_count: agg.count,
            error: {
              name: redact(agg.err.name),
              message: redact(agg.err.message),
              stack: redact(agg.err.stack || "").slice(0, 20000),
            },
            environment: import.meta.env.MODE,
            user_agent: navigator.userAgent,
          },
        }),
      });
    } catch {
      // Fire-and-forget: 400/401/403 are terminal, 429 means slow down; never retry.
    }
  }
}

/**
 * Idempotent. Call once the user is signed in. Returns an uninstall function that removes the
 * listeners, clears timers and unsent reports, and resets the probe/disabled cache so the next
 * sign-in re-probes.
 */
export function installErrorReporter(): () => void {
  if (installed) return uninstallErrorReporter;
  installed = true;
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);
  return uninstallErrorReporter;
}

function onError(e: ErrorEvent): void {
  if (e.error instanceof Error) report(e.error, e.filename);
}

function onRejection(e: PromiseRejectionEvent): void {
  const r: unknown = e.reason;
  report(r instanceof Error ? r : new Error(String(r)));
}

function uninstallErrorReporter(): void {
  window.removeEventListener("error", onError);
  window.removeEventListener("unhandledrejection", onRejection);
  generation++;
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = null;
  pending.clear();
  lastSent.clear();
  installed = false;
  resetAgentAutofixProbe();
}

/** Forget the per-session probe result so the next sign-in probes again. */
export function resetAgentAutofixProbe(): void {
  probe = null;
  disabled = false;
}
